/**
 * AI Help Desk Assistant
 * Powers the help desk with intelligent ticket analysis, responses, and learning
 */

import React, { useState, useEffect } from 'react'
import { Zap, Send, Loader, CheckCircle, AlertCircle, Brain, MessageSquare, Volume2, VolumeX, Image } from 'lucide-react'
import aiService from '@/services/aiService'
import ollamaService from '@/services/ollamaService'
import { aiContextManager } from '@/services/aiContextManager'
import { voiceService } from '@/services/voiceService'
import WebcamFeed from './WebcamFeed'

export default function AIHelpDeskAssistant({ ticket, onResponseGenerated, onAnalysisComplete }) {
  const [loading, setLoading] = useState(false)
  const [analysis, setAnalysis] = useState(null)
  const [suggestedResponse, setSuggestedResponse] = useState(null)
  const [error, setError] = useState(null)
  const [autoLearn, setAutoLearn] = useState(true)
  const [voiceEnabled, setVoiceEnabled] = useState(true)
  const [showWebcam, setShowWebcam] = useState(false)
  const [uploadedImage, setUploadedImage] = useState(null)
  const [speaking, setSpeaking] = useState(false)

  /**
   * Analyze a support ticket with AI
   */
  const analyzeTicket = async () => {
    if (!ticket) return

    setLoading(true)
    setError(null)
    setAnalysis(null)

    try {
      // Build context from ticket
      const ticketContext = {
        title: ticket.title,
        description: ticket.description,
        priority: ticket.priority,
        category: ticket.category,
        customerName: ticket.customerName,
        status: ticket.status,
      }

      // Store in AI context for learning
      aiContextManager.addContext('helpdesk_ticket', ticketContext, `Ticket #${ticket.id}`)

      // Analyze with Deepseek via Ollama
      const analysisPrompt = `You are an expert help desk support analyst. Analyze this support ticket and provide:
1. Issue Summary (1-2 sentences)
2. Root Cause (what's likely causing this)
3. Severity Assessment (Critical/High/Medium/Low)
4. Suggested Actions (3-4 bullet points)
5. Time to Resolution (estimate)

Ticket:
Title: ${ticket.title}
Description: ${ticket.description}
Priority: ${ticket.priority}
Category: ${ticket.category}

Provide structured analysis.`

      const analysisResult = await ollamaService.analyzeData(ticketContext, analysisPrompt)

      setAnalysis(analysisResult)
      onAnalysisComplete?.(analysisResult)

      return analysisResult
    } catch (err) {
      setError(`Analysis failed: ${err.message}`)
      console.error('Ticket analysis failed:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Generate AI-suggested response
   */
  const generateResponse = async () => {
    if (!ticket) return

    setLoading(true)
    setError(null)
    setSuggestedResponse(null)

    try {
      // Get support agent persona
      const supportPersona = {
        name: 'Cybertection Support',
        role: 'Support Agent',
        tone: 'professional yet empathetic',
        expertise: ['technical support', 'troubleshooting', 'customer service'],
        communication: 'clear, helpful, solution-focused',
      }

      // Get learned patterns from this support agent
      const agentPattern = aiContextManager.getUserPattern('support_agent')

      // Generate response using Deepseek
      const responsePrompt = `You are a professional support agent helping a customer with their issue.

Customer Issue: ${ticket.title}
Details: ${ticket.description}
Priority: ${ticket.priority}

Write a professional, empathetic, and helpful response that:
1. Acknowledges their issue
2. Explains initial steps to diagnose
3. Provides clear instructions
4. Offers timeline for resolution
5. Provides follow-up contact info

Keep it concise but complete (150-250 words).`

      const response = await ollamaService.generateVoiceResponse(responsePrompt, supportPersona)

      setSuggestedResponse(response)
      onResponseGenerated?.(response)

      // Record for learning
      if (autoLearn) {
        aiContextManager.recordFeedback(
          {
            type: 'helpdesk_response',
            ticketId: ticket.id,
            input: ticket.description,
            output: response,
          },
          {
            rating: 0, // Will be updated when agent rates it
            tags: ['helpdesk', ticket.category],
            userId: 'support_agent',
          }
        )
      }

      return response
    } catch (err) {
      setError(`Response generation failed: ${err.message}`)
      console.error('Response generation failed:', err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Get recommended actions
   */
  const getRecommendations = async () => {
    if (!ticket) return

    setLoading(true)
    setError(null)

    try {
      const situation = `Support ticket: ${ticket.title} - ${ticket.description}`

      const recs = await aiService.generateRecommendations(situation, {
        priority: ticket.priority,
        category: ticket.category,
        complexity: 'medium',
      })

      return recs
    } catch (err) {
      setError(`Recommendations failed: ${err.message}`)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Rate the AI response (for learning)
   */
  const rateResponse = (rating, tags = []) => {
    if (!suggestedResponse || !ticket) return

    aiContextManager.recordFeedback(
      {
        type: 'helpdesk_response',
        ticketId: ticket.id,
        input: ticket.description,
        output: suggestedResponse,
      },
      {
        rating,
        tags: ['helpdesk', ticket.category, ...tags],
        userId: 'support_agent',
        shouldFineTune: true,
      }
    )

    console.log(`✓ Response rated ${rating}/5 and logged for learning`)
  }

  /**
   * Speak the response using user's voice
   */
  const speakResponse = async () => {
    if (!suggestedResponse || !voiceEnabled) return

    try {
      setSpeaking(true)
      await voiceService.speak(suggestedResponse, {
        rate: 1.0,
        pitch: 1.0,
        volume: 1.0,
      })
    } catch (err) {
      console.error('Voice speaking failed:', err)
      alert('Voice synthesis not available in this browser')
    } finally {
      setSpeaking(false)
    }
  }

  /**
   * Analyze uploaded image with llava
   */
  const analyzeUploadedImage = async (imageFile) => {
    try {
      setLoading(true)
      setError(null)

      // Read image as base64
      const reader = new FileReader()
      reader.onload = async (e) => {
        const base64Image = e.target.result.split(',')[1]

        try {
          const analysis = await ollamaService.analyzeImage(
            base64Image,
            `Analyze this image for a support ticket. Identify any issues, problems, or details relevant to support: ${ticket.description}`
          )

          setAnalysis(`IMAGE ANALYSIS:\n${analysis}`)
          setUploadedImage(imageFile)
        } catch (err) {
          setError(`Image analysis failed: ${err.message}`)
        } finally {
          setLoading(false)
        }
      }
      reader.readAsDataURL(imageFile)
    } catch (err) {
      setError(`Failed to process image: ${err.message}`)
      setLoading(false)
    }
  }

  if (!ticket) {
    return (
      <div className="bg-slate-700/30 rounded-lg p-4 border border-slate-600 text-center text-slate-400">
        <p>Select a ticket to view AI analysis and suggestions</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* AI Controls */}
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-lg border border-blue-500/20 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-blue-300 flex items-center gap-2">
            <Brain className="w-5 h-5" />
            AI Help Desk Assistant
          </h3>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={autoLearn}
              onChange={(e) => setAutoLearn(e.target.checked)}
              className="rounded"
            />
            <span className="text-slate-300">Learn from responses</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <button
            onClick={analyzeTicket}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Brain className="w-4 h-4" />
                Analyze Ticket
              </>
            )}
          </button>

          <button
            onClick={generateResponse}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <MessageSquare className="w-4 h-4" />
                Generate Response
              </>
            )}
          </button>

          <button
            onClick={getRecommendations}
            disabled={loading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Thinking...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                Recommendations
              </>
            )}
          </button>

          <button
            onClick={() => setShowWebcam(!showWebcam)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
          >
            <Image className="w-4 h-4" />
            {showWebcam ? 'Hide' : 'Show'} Camera
          </button>
        </div>

        {/* Webcam Feed */}
        {showWebcam && (
          <div className="mb-3">
            <WebcamFeed
              height="250px"
              onVideoReady={(stream) => console.log('Video ready')}
            />
            <div className="mt-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0]
                  if (file) analyzeUploadedImage(file)
                }}
                className="text-sm text-slate-300"
              />
              <p className="text-xs text-slate-500 mt-1">Or upload an image to analyze</p>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Ticket Analysis */}
      {analysis && (
        <div className="bg-gradient-to-br from-blue-900/30 to-cyan-900/30 rounded-lg border border-blue-500/30 p-4">
          <h4 className="text-blue-300 font-semibold mb-3 flex items-center gap-2">
            <CheckCircle className="w-4 h-4" />
            AI Analysis
          </h4>
          <div className="bg-slate-800/50 rounded p-3 text-slate-300 text-sm whitespace-pre-wrap font-mono max-h-64 overflow-y-auto">
            {analysis}
          </div>
        </div>
      )}

      {/* Suggested Response */}
      {suggestedResponse && (
        <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-lg border border-purple-500/30 p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-purple-300 font-semibold flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Suggested Response
            </h4>
            <div className="flex gap-2">
              <button
                onClick={speakResponse}
                disabled={speaking || !voiceService.isSupported()}
                className="flex items-center gap-1 px-2 py-1 bg-blue-600/50 hover:bg-blue-600 disabled:opacity-50 text-blue-300 text-xs rounded transition-colors"
                title="Speak response using your voice"
              >
                {speaking ? (
                  <>
                    <Loader className="w-3 h-3 animate-spin" />
                    Speaking...
                  </>
                ) : (
                  <>
                    <Volume2 className="w-3 h-3" />
                    Speak
                  </>
                )}
              </button>
              <span className="text-xs text-slate-400">Deepseek Generated</span>
            </div>
          </div>

          <div className="bg-slate-800/50 rounded p-3 text-slate-300 text-sm mb-4 max-h-64 overflow-y-auto">
            {suggestedResponse}
          </div>

          {/* Rating Controls */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Rate this response:</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(rating => (
                <button
                  key={rating}
                  onClick={() => rateResponse(rating, rating >= 4 ? ['helpful'] : ['needs-improvement'])}
                  className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                    rating <= 2
                      ? 'hover:bg-red-500/30 text-red-300'
                      : rating <= 3
                      ? 'hover:bg-yellow-500/30 text-yellow-300'
                      : 'hover:bg-green-500/30 text-green-300'
                  }`}
                >
                  {rating} ★
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Learning Status */}
      <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600 text-xs text-slate-400">
        <p>
          💡 <strong>Learning:</strong> {autoLearn ? 'Enabled - responses are logged for AI improvement' : 'Disabled - responses are not being tracked'}
        </p>
      </div>
    </div>
  )
}
